import { useQuery } from '@apollo/client';
import { HistoryActionMenuView } from 'common/components/History';
import {
  MenuPropertiesCount,
  getUpdatedPivotActionItems,
} from 'common/components/Modals/utils';
import {
  PivotDisplay,
  PivotMenuItemProps,
} from 'common/components/PivotDisplay';
import { GlobalActions } from 'common/constants';
import React, { useCallback, useState } from 'react';
import { Files } from './Files';
import { Pivot, PivotItem, Stack, makeStyles } from '@fluentui/react';
import { PivotCustomRender } from 'common/components/PivotComponent';
import { pivotActionItemsTags as pivotActionItems } from 'common/components/ActionMenu/PivotActionItems';
import {
  ListTravelAuthorizationNotes,
  ListTravelAuthorizationNotesVariables,
} from 'common/graphql/__generated__/ListTravelAuthorizationNotes';
import { TravelPlanDetailProps } from '..';
import { Tags } from './Tags';
import { Notes } from './Notes';

import ENTITY_NOTES_LIST from '../../../../../graphql/ListTravelAuthorizationNotes.graphql';
type ActionMenuProps = Pick<TravelPlanDetailProps, 'travelAuthorization'>;

const useStyles = makeStyles(() => ({
  centerAlign: {
    alignItems: 'center',
  },
}));

export const ActionMenu: React.FC<ActionMenuProps> = ({
  travelAuthorization,
}) => {
  const styles = useStyles();
  const {
    id,
    entityDocumentsByEntityId,
    notesByEntityId,
    travelAuthorizationHistoriesByEntityId,
    entityTagsByEntityId,
  } = { ...travelAuthorization };
  let updatedPivotActionItems: PivotMenuItemProps[] = [];
  const [isMenuExpanded, setIsMenuExpanded] = useState<boolean>(true);
  const [selectedKey, setSelectedKey] = useState<string>(GlobalActions.attach);

  const { data: entityNotesData, refetch: entityNotesListRefetch } = useQuery<
    ListTravelAuthorizationNotes,
    ListTravelAuthorizationNotesVariables
  >(ENTITY_NOTES_LIST, {
    variables: {
      id: id!,
    },
  });

  const { nodes } = {
    ...entityNotesData?.travelAuthorization?.notesByEntityId,
  };

  const getEntityNotes = () => {
    const variables: ListTravelAuthorizationNotesVariables = {
      id: id!,
    };
    entityNotesListRefetch(variables);
  };

  const getEntityNotesMemo = useCallback(getEntityNotes, [id!]);

  const menuCountArray: MenuPropertiesCount[] = [
    {
      name: 'Files',
      iconName: 'Attach',
      count: entityDocumentsByEntityId?.nodes.length || 0,
    },
    {
      name: 'Notes',
      iconName: 'Page',
      count: notesByEntityId?.nodes?.length || 0,
    },
    {
      name: 'History',
      iconName: 'History',
      count: travelAuthorizationHistoriesByEntityId?.totalCount || 0,
    },
    {
      name: 'Tags',
      iconName: 'Tag',
      count: entityTagsByEntityId?.nodes?.length || 0,
    },
  ];

  updatedPivotActionItems = travelAuthorization
    ? getUpdatedPivotActionItems(menuCountArray, pivotActionItems)
    : (updatedPivotActionItems = pivotActionItems);

  const getSelectedSection = (key: string) => {
    switch (key) {
      case GlobalActions.attach:
        return <Files fileListData={entityDocumentsByEntityId!} />;
      case GlobalActions.notes:
        return (
          <Notes
            travelAuthorizationId={id!}
            data={nodes || []}
            listRefreshRequest={getEntityNotesMemo}
          />
        );
      case GlobalActions.history:
        return (
          <HistoryActionMenuView
            moduleName="Travel Authorization"
            historyData={travelAuthorizationHistoriesByEntityId?.nodes || []}
          />
        );
      case GlobalActions.tags:
        return <Tags travelAuthorizationId={id || ''} />;
      default:
        return null;
    }
  };

  return (
    <Stack tokens={{ childrenGap: 20 }}>
      <Stack tokens={{ padding: '10px 20px' }}>
        {isMenuExpanded ? (
          <Stack>
            <Stack horizontalAlign="space-between" horizontal>
              <Stack horizontal className={styles.centerAlign}>
                <Pivot
                  selectedKey={selectedKey}
                  onLinkClick={(item: PivotItem | undefined) => {
                    setSelectedKey(item?.props.itemKey || GlobalActions.attach);
                  }}
                >
                  {updatedPivotActionItems.map((item, index) => {
                    return (
                      <PivotItem
                        key={index}
                        itemKey={item.itemKey}
                        itemIcon={item.iconName}
                        headerText={item.name}
                        itemCount={item.count}
                        onRenderItemLink={PivotCustomRender}
                      />
                    );
                  })}
                </Pivot>
              </Stack>
            </Stack>
            <Stack>{getSelectedSection(selectedKey)}</Stack>
          </Stack>
        ) : (
          <Stack horizontalAlign="space-between" horizontal>
            <Stack horizontal verticalAlign="center">
              <Stack horizontal verticalAlign="center">
                {updatedPivotActionItems.map((item, index) => {
                  return (
                    <PivotDisplay
                      key={index}
                      {...item}
                      onPivotClick={() => {
                        setSelectedKey(item.itemKey!);
                        setIsMenuExpanded(true);
                      }}
                    />
                  );
                })}
              </Stack>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
